import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { FakeLink } from '@hivebrite/design-system/Link';
import { Slider } from '@hivebrite/design-system/Slider';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { GroupThumbnail } from 'hb-react/frontoffice/components/GroupThumbnail';
import GroupLoadingCard from 'hb-react/frontoffice/components/GroupThumbnail/GroupLoadingCard';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { getAppPageConfig } from 'hb-react/frontoffice/utils/page';
import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';
import Grid, { SliderRow } from 'hb-react/shared/components/DesignSystem/Grid';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import { CARDS_PER_ROW, CARDS_COL_WIDTH } from './constants';
import useSuggestedGroups from './useSuggestedGroups';
import { customArrowStyle } from './utils/customArrowStyle';

const SuggestedGroups = ({ isInFeed = false, onLoadedEmpty }) => {
  const outboxedFontTheme = useOutboxedFontTheme();
  const breakpoint = useBreakpoint();
  const { currentNetwork } = useDefaultAppContexts();
  const { flagUeA11YSliders } = useFeatureFlag();

  const { isLoaded, onLoadMore, onSeeAll, topics, totalTopics } =
    useSuggestedGroups();

  const groupsTabName = useMemo(
    () => getAppPageConfig('topics', currentNetwork)?.menu_name || __('groups'),
    [currentNetwork],
  );

  if (isLoaded && totalTopics === 0) {
    if (isInFeed && onLoadedEmpty) {
      onLoadedEmpty();
    }

    return null;
  }

  const customWidth = isInFeed ? 'calc(100% + 16px)' : '100%';
  const customMarginLeft = isInFeed
    ? { _: 0 }
    : {
        _: 4,
        m: 0,
        l: -1,
        xl: 0,
      };
  const title = s__(
    'LiveFeedSuggestedGroups|Suggested %{groupsTabName} for you',
  ).i__({ groupsTabName });

  return flagUeA11YSliders ? (
    <Box flexDirection="column" mt={6}>
      <Box
        flexDirection="column"
        pl={
          isInFeed
            ? { _: 0 }
            : {
                _: 4,
                m: 0,
                l: -1,
                xl: 0,
              }
        }
        pr={{
          _: 4,
          m: 0,
        }}
        mb={4}
        data-testid="live-feed-suggested-groups"
      >
        <ThemeProvider
          theme="fo"
          fontBrightness={outboxedFontTheme.fontBrightness}
        >
          <Box mt={6} mb={4}>
            <ClampedLinkWorkaround clamp={1} tagName="h2" useNativeClamp>
              <Box font="h2-semibold" as="span" display="inline">
                {title}
                <span>&nbsp;·&nbsp;</span>
              </Box>
            </ClampedLinkWorkaround>
            <FakeLink
              font="h2-semibold"
              as="span"
              display="inline"
              onClick={onSeeAll}
              onKeyPress={onEnterOrSpacePress(onSeeAll)}
            >
              {s__('LiveFeedSuggestedGroups|See all')}
            </FakeLink>
          </Box>
        </ThemeProvider>
        <Slider
          items={topics}
          total={topics.length}
          perPageBreakpoints={CARDS_PER_ROW}
          ItemComponent={GroupThumbnail}
          LoadingItemComponent={GroupLoadingCard}
          onLoadMore={onLoadMore}
        />
      </Box>
    </Box>
  ) : (
    <Box flexDirection="column" mt={6} width={{ _: customWidth, m: '100%' }}>
      <Box
        flexDirection="column"
        ml={customMarginLeft}
        mr={{
          _: '-4px', // spacing between SliderRow Col items is always to the right, so we need to counter-act it
          m: 0,
        }}
        width={{ _: '100%' }}
        data-testid="live-feed-suggested-groups"
      >
        <ThemeProvider
          theme="fo"
          fontBrightness={outboxedFontTheme.fontBrightness}
        >
          <Box mt={6}>
            <ClampedLinkWorkaround clamp={1} tagName="h2" useNativeClamp>
              <Box font="h2-semibold" as="span" display="inline">
                {title}
                <span>&nbsp;·&nbsp;</span>
              </Box>
            </ClampedLinkWorkaround>
            <FakeLink
              font="h2-semibold"
              as="span"
              display="inline"
              onClick={onSeeAll}
              onKeyPress={onEnterOrSpacePress(onSeeAll)}
            >
              {s__('LiveFeedSuggestedGroups|See all')}
            </FakeLink>
          </Box>
        </ThemeProvider>
        <Grid
          width="100%"
          mx={0}
          mt={{ _: 0, m: -1, l: 0 }}
          mb={{ _: 3, l: 4 }}
        >
          <SliderRow
            items={topics}
            total={topics.length}
            perPageBreakpoints={CARDS_PER_ROW}
            colWidthBreakpoints={CARDS_COL_WIDTH}
            ItemComponent={(group) => <GroupThumbnail {...group} />}
            LoadingItemComponent={GroupLoadingCard}
            onLoadMore={onLoadMore}
            customArrowStyle={customArrowStyle(breakpoint)}
            itemPadding={{
              _: '19px', // exact value from the UI mock-ups
              m: 0,
            }}
            overflowSliderContainer="hidden"
          />
        </Grid>
      </Box>
    </Box>
  );
};

SuggestedGroups.propTypes = {
  isInFeed: PropTypes.bool,
  onLoadedEmpty: PropTypes.func,
};

SuggestedGroups.defaultProps = {
  isInFeed: false,
};

export default SuggestedGroups;
